// Exports: default
import React, { useCallback, useMemo, useState } from "react";
import TaskGroupDetailComponent from "./TaskGroupDetailComponent";
import TaskDetailComponent from "./TaskDetailComponent";
import SubtaskDetailComponent from "./SubTaskDetailComponent";
import KanbanView from "./KanbanView";
import MainTableView from "./MainTableView";
import CustomGantt from "./CustomGantt";
import useScrollAnchor from "hooks/useScrollAnchor";
import useTaskGroups from "hooks/useTaskGroups";
import {
  getAvatarColor,
  getInitials,
} from "utils/tasks/taskHelpers";

const TaskHome = () => {
  const siteId = Number(localStorage.getItem("site"));
  const token = localStorage.getItem("access");

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token]
  );

  const {
    taskGroups,
    refreshing,
    statusOptions,
    statePersistentRefresh,
    fetchTasksForGroup,
    fetchSubtasksForTask,
    onToggleGroup,
    onToggleTask,
    addTaskGroup,
    handleAddTask,
    handleAddSubtask,
    deleteSubtask,
    onGroupEditStart,
    onGroupEditCancel,
    onGroupEditSubmit,
    editingGroupId,
    editingGroupName,
    onTaskEditStart,
    onTaskEditCancel,
    onTaskEditSubmit,
    editingTaskId,
    editingTaskName,
    onSubEditStart,
    onSubEditCancel,
    onSubEditSubmit,
    editingSubId,
    editingSubName,
    onTaskStatusChange,
    onTaskPriorityChange,
    onToggleSubComplete,
    handleTaskDateChange,
    handleSubtaskDateChange,
    mergeGroupAllocatedMembers,
    openStatusDropdown,
    setOpenStatusDropdown,
    openPriorityDropdown,
    setOpenPriorityDropdown,
    getStatusProportions,
  } = useTaskGroups(siteId, authHeaders);

  const [activeView, setActiveView] = useState("Main Table");
  const [selectedTaskGroup, setSelectedTaskGroup] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedSubtask, setSelectedSubtask] = useState(null);
  const [showAddTaskGroupInput, setShowAddTaskGroupInput] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(null);
  const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(null);

  const { scrollContainerRef, captureScrollAnchor, preserveScroll } =
    useScrollAnchor([
      taskGroups,
      activeView,
      selectedTaskGroup,
      selectedTask,
      selectedSubtask,
      showAddTaskGroupInput,
      showAddTaskModal,
      showAddSubtaskModal,
      editingGroupId,
      editingTaskId,
      editingSubId,
      openStatusDropdown,
      openPriorityDropdown,
    ]);

  const handleRefresh = useCallback(
    () => preserveScroll(() => statePersistentRefresh()),
    [preserveScroll, statePersistentRefresh]
  );

  const handleRefetchTasks = useCallback(
    (groupId) =>
      preserveScroll(`[data-scroll-anchor="group-${groupId}"]`, () =>
        fetchTasksForGroup(groupId)
      ),
    [fetchTasksForGroup, preserveScroll]
  );

  const handleRefetchSubtasks = useCallback(
    (groupId, taskId) =>
      preserveScroll(`[data-scroll-anchor="task-${taskId}"]`, () =>
        fetchSubtasksForTask(groupId, taskId)
      ),
    [fetchSubtasksForTask, preserveScroll]
  );

  const handleSetSelectedTaskGroup = useCallback(
    (value) => preserveScroll(() => setSelectedTaskGroup(value)),
    [preserveScroll]
  );

  const handleSetSelectedTask = useCallback(
    (value) => preserveScroll(() => setSelectedTask(value)),
    [preserveScroll]
  );

  const handleSetSelectedSubtask = useCallback(
    (value) => preserveScroll(() => setSelectedSubtask(value)),
    [preserveScroll]
  );

  const handleAddTaskGroupClick = useCallback(
    () => preserveScroll(() => setShowAddTaskGroupInput(true)),
    [preserveScroll]
  );

  const handleAddTaskClick = useCallback(
    (groupId) =>
      preserveScroll(`[data-scroll-anchor="group-${groupId}"]`, () => {
        setShowAddTaskModal(groupId);
        setShowAddSubtaskModal(null);
      }),
    [preserveScroll]
  );

  const handleAddSubtaskClick = useCallback(
    (_groupId, taskId) =>
      preserveScroll(`[data-scroll-anchor="task-${taskId}"]`, () => {
        setShowAddSubtaskModal(taskId);
        setShowAddTaskModal(null);
      }),
    [preserveScroll]
  );

  const handleAssignApprovers = useCallback(async () => {}, []);
  const handleAddTaskUpdate = useCallback(async () => {}, []);

  return (
    <div className="bg-[#F0F2F5] h-full">
      <div className="flex items-center justify-between p-2 border-b">
        <div className="flex items-center space-x-4">
          <button
            className={`px-4 py-2 ${
              activeView === "Main Table"
                ? "text-purple-700 border-b-2 border-purple-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveView("Main Table")}
          >
            Main Table
          </button>
          <button
            className={`px-4 py-2 ${
              activeView === "Timeline"
                ? "text-purple-700 border-b-2 border-purple-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveView("Timeline")}
          >
            Timeline
          </button>
          <button
            className={`px-4 py-2 ${
              activeView === "Kanban"
                ? "text-purple-700 border-b-2 border-purple-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveView("Kanban")}
          >
            Kanban
          </button>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        onMouseDownCapture={captureScrollAnchor}
        onTouchStartCapture={captureScrollAnchor}
        className="flex-1 h-[84vh] overflow-y-scroll"
        style={{
          scrollbarWidth: "none",
        }}
      >
        {activeView === "Timeline" && <CustomGantt />}

        {activeView === "Kanban" && (
          <KanbanView
            taskGroups={taskGroups}
            refetchTasks={handleRefetchTasks}
            onTaskStatusChange={onTaskStatusChange}
            getInitials={getInitials}
            getAvatarColor={getAvatarColor}
          />
        )}

        {activeView === "Main Table" && (
          <MainTableView
            taskGroups={taskGroups}
            hasGroups={taskGroups.length > 0}
            refetchTasks={handleRefetchTasks}
            refetchSubtasks={handleRefetchSubtasks}
            mergeGroupAllocatedMembers={mergeGroupAllocatedMembers}
            onAddTaskGroupClick={handleAddTaskGroupClick}
            onAddTaskClick={handleAddTaskClick}
            handleAddTask={handleAddTask}
            showAddTaskModal={showAddTaskModal}
            setShowAddTaskModal={setShowAddTaskModal}
            onToggleGroup={onToggleGroup}
            onToggleTask={onToggleTask}
            refreshing={refreshing}
            onRefresh={handleRefresh}
            onAddSubtaskClick={handleAddSubtaskClick}
            showAddSubtaskModal={showAddSubtaskModal}
            setShowAddSubtaskModal={setShowAddSubtaskModal}
            handleAddSubtask={handleAddSubtask}
            onToggleSubComplete={onToggleSubComplete}
            onTaskStatusChange={onTaskStatusChange}
            openStatusDropdown={openStatusDropdown}
            setOpenStatusDropdown={setOpenStatusDropdown}
            statusOptions={statusOptions}
            getInitials={getInitials}
            onTaskPriorityChange={onTaskPriorityChange}
            openPriorityDropdown={openPriorityDropdown}
            setOpenPriorityDropdown={setOpenPriorityDropdown}
            getAvatarColor={getAvatarColor}
            getStatusProportions={getStatusProportions}
            editingGroupId={editingGroupId}
            editingGroupName={editingGroupName}
            onGroupEditStart={onGroupEditStart}
            onGroupEditCancel={onGroupEditCancel}
            onGroupEditSubmit={onGroupEditSubmit}
            editingTaskId={editingTaskId}
            editingTaskName={editingTaskName}
            onTaskEditStart={onTaskEditStart}
            onTaskEditCancel={onTaskEditCancel}
            onTaskEditSubmit={onTaskEditSubmit}
            editingSubId={editingSubId}
            editingSubName={editingSubName}
            onSubEditStart={onSubEditStart}
            onSubEditCancel={onSubEditCancel}
            onSubEditSubmit={onSubEditSubmit}
            deleteSubtask={deleteSubtask}
            setSelectedTaskGroup={handleSetSelectedTaskGroup}
            selectedTaskGroup={selectedTaskGroup}
            selectedTask={selectedTask}
            selectedSubtask={selectedSubtask}
            setSelectedTask={handleSetSelectedTask}
            setSelectedSubtask={handleSetSelectedSubtask}
            handleTaskDateChange={handleTaskDateChange}
            handleSubtaskDateChange={handleSubtaskDateChange}
            preserveScroll={preserveScroll}
            showAddTaskGroupInput={showAddTaskGroupInput}
            setShowAddTaskGroupInput={setShowAddTaskGroupInput}
            addTaskGroup={addTaskGroup}
          />
        )}
      </div>

      {selectedTaskGroup && (
        <TaskGroupDetailComponent
          taskGroup={selectedTaskGroup}
          onClose={() => setSelectedTaskGroup(null)}
          onAssignApprovers={handleAssignApprovers}
          onAddUpdate={handleAddTaskUpdate}
          refetchTaskGroups={handleRefresh}
        />
      )}

      {selectedTask && (
        <TaskDetailComponent
          task={selectedTask}
          refetchTasks={handleRefetchTasks}
          refetchSubtasks={handleRefetchSubtasks}
          onClose={() => setSelectedTask(null)}
          onOpenSubtask={handleSetSelectedSubtask}
          onAssignApprovers={handleAssignApprovers}
          onAddUpdate={handleAddTaskUpdate}
        />
      )}

      {selectedSubtask && (
        <div className="fixed inset-y-0 right-0 top-0 z-50 w-1/2">
          <SubtaskDetailComponent
            refetchSubtasks={handleRefetchSubtasks}
            subtask={selectedSubtask}
            taskId={selectedSubtask.task_id}
            onClose={() => setSelectedSubtask(null)}
          />
        </div>
      )}
    </div>
  );
};

export default TaskHome;
